import type BackendClient from '../BackendClient/BackendClient'
import type { components } from '../BackendClient/generated-schema'

export type RepresentativeImageResponse =
  components['schemas']['RepresentativeImageResponse']
export type CultureLineageRepresentativeImagesResponse =
  components['schemas']['CultureLineageRepresentativeImagesResponse']
export type CertificateOfAnalysisResponse =
  components['schemas']['CertificateOfAnalysisResponse']

class RepresentativeImagesAPI {
  client: BackendClient

  constructor(client: BackendClient) {
    this.client = client
  }

  /**
   * Convert a Blob to base64 string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
          const base64 = reader.result.split(',')[1]
          resolve(base64)
        } else {
          reject(new Error('Failed to convert blob to base64'))
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  /**
   * Upload a representative image for a culture observation
   */
  async uploadRepresentativeImage(
    obsId: string,
    file: Blob,
    metadata: {
      contrast?: number
      brightness?: number
      saturation?: number
      zoomLevel?: number
      panX?: number
      panY?: number
    } = {},
  ): Promise<RepresentativeImageResponse> {
    // Convert blob to base64
    const base64Image = await this.blobToBase64(file)

    // Send as JSON body which works with openapi-fetch
    return await this.client.POST('/api/representative-images/upload', {
      body: {
        obs_id: obsId,
        file_base64: base64Image,
        filename: `rep-img-${Date.now()}.jpg`,
        content_type: 'image/jpeg',
        contrast: metadata.contrast ?? 100,
        brightness: metadata.brightness ?? 100,
        zoom_level: metadata.zoomLevel ?? 1,
        pan_x: metadata.panX ?? 0.5,
        pan_y: metadata.panY ?? 0.5,
      },
    })
  }

  /**
   * Get representative image for an observation
   */
  async getRepresentativeImage(obsId: string): Promise<RepresentativeImageResponse> {
    return await this.client.GET('/api/representative-images/obs/{obs_id}', {
      params: {
        path: { obs_id: obsId },
      },
    })
  }

  /**
   * Get all representative images for a culture and its lineage
   */
  async getCultureLineageRepresentativeImages(
    cultureExtId: string,
  ): Promise<CultureLineageRepresentativeImagesResponse> {
    return await this.client.GET('/api/representative-images/culture/{culture_extid}', {
      params: {
        path: { culture_extid: cultureExtId },
      },
    })
  }

  /**
   * Generate Certificate of Analysis report from representative images
   */
  async generateCertificateOfAnalysis(
    obsIds: string[],
  ): Promise<CertificateOfAnalysisResponse> {
    return await this.client.POST(
      '/api/representative-images/certificate-of-analysis/generate',
      {
        body: {
          obs_ids: obsIds,
        },
      },
    )
  }

  /**
   * Delete a representative image by its ID
   */
  async deleteRepresentativeImage(imageId: string): Promise<void> {
    await this.client.DELETE('/api/representative-images/{image_id}', {
      params: {
        path: { image_id: imageId },
      },
    })
  }
}

export default RepresentativeImagesAPI
